import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, fmtError } from "@/lib/api";
import { AuthShell, Field } from "@/components/AuthShell";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) return setError("Password must be at least 6 characters.");
    if (form.password !== form.confirm) return setError("Passwords do not match.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: form.password });
      setDone(true);
      toast.success("Password updated. Please sign in.");
      setTimeout(() => navigate("/auth/sign-in", { replace: true }), 1500);
    } catch (err) { setError(fmtError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  if (!token)
    return (
      <AuthShell title="Invalid link" subtitle="This password reset link is missing or malformed.">
        <Link to="/auth/forgot-password" className="w-full bg-[#F97316] text-white rounded-2xl py-4 font-bold min-h-[56px] flex items-center justify-center">Request a new link</Link>
      </AuthShell>
    );

  if (done)
    return (
      <AuthShell title="Password updated" subtitle="You can now sign in with your new password.">
        <div className="ll-fade-up flex flex-col items-center text-center gap-4 py-4">
          <CheckCircle2 className="w-14 h-14 text-[#16A34A]" />
          <Link to="/auth/sign-in" className="w-full bg-[#F97316] text-white rounded-2xl py-4 font-bold min-h-[56px] flex items-center justify-center">Go to Sign In</Link>
        </div>
      </AuthShell>
    );

  return (
    <AuthShell title="Set a new password" subtitle="Choose a strong password for your account.">
      <form onSubmit={submit} className="ll-fade-up" data-testid="reset-password-form">
        <Field label="New password" testId="reset-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
        <Field label="Confirm password" testId="reset-password-confirm" type="password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} placeholder="••••••••" />
        {error && <p data-testid="reset-error" className="text-[#DC2626] text-sm font-medium mb-4 bg-[#DC2626]/10 rounded-xl p-3">{error}</p>}
        <button type="submit" disabled={busy} data-testid="reset-submit" className="w-full bg-[#F97316] text-white rounded-2xl py-4 font-bold text-lg min-h-[56px] flex items-center justify-center disabled:opacity-70">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Update password"}
        </button>
      </form>
      <p className="text-center mt-8"><Link to="/auth/sign-in" className="font-bold text-[#F97316]">Back to sign in</Link></p>
    </AuthShell>
  );
}
